<template>
  <div class="container-cantico">
    <div class="header-section">
      <h1 class="title">{{ $t('cantico.title') }}</h1>
      <p class="subtitle">{{ $t('cantico.subtitle') }}</p>
    </div>

    <div class="language-selector-box">
      <label for="language-select">{{ $t('cantico.headerTranslations') }}:</label>
      <div class="custom-select">
        <button class="select-button" @click="dropdownOpen = !dropdownOpen">
          <img :src="`https://flagcdn.com/w20/${getCountryCode(currentLocale)}.png`" :alt="currentLocale" class="flag-icon" />
          <span>{{ getLanguageName(currentLocale) }}</span>
          <span class="arrow" :class="{ open: dropdownOpen }">▼</span>
        </button>
        <div v-if="dropdownOpen" class="dropdown-menu">
          <div 
            v-for="lang in languages" 
            :key="lang.code"
            class="dropdown-item"
            @click="selectLanguage(lang.code)"
          >
            <img :src="`https://flagcdn.com/w20/${lang.country}.png`" :alt="lang.code" class="flag-icon" />
            <span>{{ lang.name }}</span>
          </div>
        </div>
      </div>
    </div>

    <table class="comparison-table">
      <thead>
        <tr>
          <th class="col-original">{{ $t('cantico.headerOriginal') }}</th>
          <th class="col-divider"></th>
          <th class="col-translation">{{ $t('cantico.headerTranslations') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td class="text-content">
            <pre class="poem-text">{{ originalText }}</pre>
          </td>
          <td class="divider-cell">
            <div class="vertical-divider"></div>
          </td>
          <td class="text-content translation-column">
            <pre class="poem-text">{{ $t('cantico.text') }}</pre>
          </td>
        </tr>
      </tbody>
    </table>
    
    <div class="video-section">
      <h2>Il Cantico delle Creature</h2>
      <div class="video-container">
        <video controls width="100%" height="auto">
          <source src="/Cantico_dalle_Creature.mp4" type="video/mp4">
          Il tuo browser non supporta il tag video.
        </video>
      </div>
    </div>
  </div>
</template>

<script>
import { useI18n } from 'vue-i18n'
import { originalText } from '../i18nt'

export default {
  name: 'TestoCantico',
  data() {
    return {
      dropdownOpen: false,
      languages: [
        { code: 'it', name: 'Italiano', country: 'it' },
        { code: 'ru', name: 'Русский', country: 'ru' },
        { code: 'uk', name: 'Українська', country: 'ua' },
        { code: 'hy', name: 'Հայերեն', country: 'am' },
        { code: 'zh', name: '中文', country: 'cn' },
        { code: 'fr', name: 'Français', country: 'fr' },
        { code: 'pl', name: 'Polski', country: 'pl' }
      ]
    }
  },
  setup() {
    const { locale, t } = useI18n()
    
    return {
      originalText,
      t,
      locale
    }
  },
  computed: {
    currentLocale: {
      get() {
        return this.locale
      },
      set(value) {
        this.$i18n.locale = value
      }
    }
  },
  methods: {
    selectLanguage(lang) {
      this.currentLocale = lang
      this.dropdownOpen = false
    },
    getCountryCode(locale) {
      const lang = this.languages.find(l => l.code === locale)
      return lang ? lang.country : 'it'
    },
    getLanguageName(locale) {
      const lang = this.languages.find(l => l.code === locale)
      return lang ? lang.name : 'Italiano'
    }
  },
  mounted() {
    document.addEventListener('click', (e) => {
      if (!this.$el.querySelector('.custom-select')?.contains(e.target)) {
        this.dropdownOpen = false
      }
    })
  }
}
</script>

<style scoped>
.container-cantico {
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
  background: linear-gradient(135deg, rgba(20, 20, 20, 0.95) 0%, rgba(30, 30, 30, 0.98) 100%);
  border-radius: 12px;
}

.header-section {
  text-align: center;
  margin-bottom: 50px;
  animation: fadeInDown 0.6s ease-out;
}

.title {
  font-size: 2.5rem;
  color: #dc2626;
  margin: 0 0 10px 0;
  text-shadow: 0 2px 8px rgba(220, 38, 38, 0.3);
  font-weight: 700;
}

.subtitle {
  font-size: 1.3rem;
  color: #a3a3a3;
  margin: 0;
  font-style: italic;
}

.language-selector-box {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
  margin-bottom: 40px;
  padding: 20px;
  background: rgba(220, 38, 38, 0.08);
  border: 1px solid rgba(220, 38, 38, 0.3);
  border-radius: 8px;
  animation: fadeIn 0.6s ease-out 0.2s both;
  position: relative;
  z-index: 999;
}

.language-selector-box label {
  color: #dc2626;
  font-weight: 600;
  font-size: 1rem;
}

.custom-select {
  position: relative;
  display: inline-block;
  width: auto;
}

.select-button {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 15px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid #dc2626;
  border-radius: 6px;
  color: #e5e5e5;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 180px;
  justify-content: space-between;
}

.select-button:hover {
  background: rgba(220, 38, 38, 0.1);
  box-shadow: 0 0 15px rgba(220, 38, 38, 0.3);
}

.select-button:focus {
  outline: none;
  box-shadow: 0 0 20px rgba(220, 38, 38, 0.5);
  background: rgba(220, 38, 38, 0.15);
}

.flag-icon {
  width: 24px;
  height: 16px;
  border-radius: 2px;
  object-fit: cover;
}

.select-button .flag-icon {
  flex-shrink: 0;
}

.arrow {
  transition: transform 0.3s ease;
  font-size: 0.8rem;
  color: #dc2626;
}

.arrow.open {
  transform: rotate(180deg);
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: #2a2a2a;
  border: 1px solid #dc2626;
  border-radius: 6px;
  margin-top: 5px;
  z-index: 1000;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.5);
  overflow-y: auto;
  overflow-x: hidden;
  max-height: 400px;
  animation: dropdownSlide 0.2s ease-out;
  min-width: 180px;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 15px;
  color: #e5e5e5;
  cursor: pointer;
  transition: all 0.2s ease;
  border-bottom: 1px solid rgba(220, 38, 38, 0.1);
  white-space: nowrap;
}

.dropdown-item:last-child {
  border-bottom: none;
}

.dropdown-item:hover {
  background: rgba(220, 38, 38, 0.2);
  color: white;
}

.dropdown-item .flag-icon {
  flex-shrink: 0;
}

@keyframes dropdownSlide {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.comparison-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  border-radius: 20px;
  min-width: 800px;
  table-layout: fixed;
  animation: fadeInUp 0.6s ease-out 0.3s both;
  overflow-x: auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
}

table.comparison-table {
  margin: 0;
  display: table;
}

thead tr {
  background: linear-gradient(135deg, rgba(220, 38, 38, 0.95) 0%, rgba(185, 28, 28, 0.95) 100%);
  border-bottom: 3px solid rgba(220, 38, 38, 0.8);
  position: sticky;
  top: 0;
  z-index: 10;
  overflow: hidden;
}

th {
  padding: 18px 15px;
  text-align: center;
  color: white;
  font-weight: 700;
  font-size: 0.95rem;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  background: linear-gradient(180deg, rgba(220, 38, 38, 1) 0%, rgba(185, 28, 28, 0.95) 100%);
}

th:first-child {
  border-radius: 20px 0 0 0;
}

th:last-child {
  border-radius: 0 20px 0 0;
}

.col-original {
  width: 48%;
}

.col-translation {
  width: 48%;
}

.col-divider {
  width: 4%;
  background: transparent;
  padding: 0 !important;
}

tbody tr {
  background: rgba(20, 20, 20, 0.6);
  border-bottom: 1px solid rgba(220, 38, 38, 0.15);
}

tbody tr:last-child td {
  border-radius: 0 0 20px 0;
}

tbody tr:last-child td:first-child {
  border-radius: 0 0 0 20px;
}

td {
  padding: 25px 20px;
  border: none;
  vertical-align: top;
  background: rgba(20, 20, 20, 0.5);
}

td.text-content {
  width: 48%;
  background: rgba(18, 18, 20, 0.7);
}

.divider-cell {
  width: 4%;
  padding: 0 !important;
  background: transparent;
  position: relative;
  overflow: visible;
}

.vertical-divider {
  height: 100%;
  width: 1px;
  background: linear-gradient(180deg, 
    rgba(220, 38, 38, 0) 0%, 
    rgba(220, 38, 38, 0.4) 20%, 
    rgba(220, 38, 38, 0.6) 50%, 
    rgba(220, 38, 38, 0.4) 80%, 
    rgba(220, 38, 38, 0) 100%);
  box-shadow: 0 0 8px rgba(220, 38, 38, 0.3);
  margin: 0 auto;
}

.translation-column {
  background: rgba(20, 20, 28, 0.5);
}

.poem-text {
  margin: 0;
  color: #d4d4d8;
  font-family: 'Georgia', 'Garamond', serif;
  font-size: 0.95rem;
  line-height: 1.9;
  white-space: pre-wrap;
  word-wrap: break-word;
  word-break: break-word;
  text-align: left;
  overflow-wrap: break-word;
  letter-spacing: 0.3px;
}

@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@media (max-width: 1100px) {
  .comparison-table {
    min-width: 900px;
  }

  .poem-text {
    font-size: 0.9rem;
    line-height: 1.8;
  }

  th {
    font-size: 0.9rem;
    padding: 15px 12px;
  }

  td {
    padding: 20px 15px;
  }
}

@media (max-width: 968px) {
  .container-cantico {
    padding: 25px 15px;
  }

  .comparison-table {
    min-width: 850px;
  }

  .poem-text {
    font-size: 0.85rem;
    line-height: 1.75;
  }

  th {
    font-size: 0.85rem;
    padding: 12px 10px;
  }

  td {
    padding: 18px 12px;
  }
}

@media (max-width: 768px) {
  .container-cantico {
    padding: 20px 10px;
  }

  .title {
    font-size: 1.8rem;
  }

  .subtitle {
    font-size: 1rem;
  }

  .language-selector-box {
    flex-direction: column;
    gap: 10px;
  }

  .language-select {
    width: 100%;
  }

  .comparison-table {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    border-radius: 15px;
  }

  table.comparison-table {
    min-width: 100%;
    table-layout: auto;
  }

  th:first-child {
    border-radius: 15px 0 0 0;
  }

  th:last-child {
    border-radius: 0 15px 0 0;
  }

  tbody tr:last-child td:first-child {
    border-radius: 0 0 0 15px;
  }

  tbody tr:last-child td {
    border-radius: 0 0 15px 0;
  }

  .col-original,
  .col-translation {
    width: auto;
  }

  td.text-content {
    width: auto;
  }

  .poem-text {
    font-size: 0.8rem;
    line-height: 1.7;
  }

  th {
    font-size: 0.8rem;
    padding: 10px 8px;
  }

  td {
    padding: 15px 10px;
  }
}

@media (max-width: 640px) {
  .container-cantico {
    padding: 15px 5px;
  }

  .comparison-table {
    border-radius: 12px;
  }

  th:first-child {
    border-radius: 12px 0 0 0;
  }

  th:last-child {
    border-radius: 0 12px 0 0;
  }

  tbody tr:last-child td:first-child {
    border-radius: 0 0 0 12px;
  }

  tbody tr:last-child td {
    border-radius: 0 0 12px 0;
  }

  .title {
    font-size: 1.5rem;
  }

  .subtitle {
    font-size: 0.9rem;
  }

  .poem-text {
    font-size: 0.75rem;
    line-height: 1.6;
  }

  th {
    font-size: 0.7rem;
    padding: 8px 5px;
  }

  td {
    padding: 12px 8px;
  }
}

.video-section {
  margin-top: 80px;
  margin-bottom: 80px;
  animation: fadeInUp 0.6s ease-out 0.4s both;
}

.video-section h2 {
  color: #dc2626;
  text-align: center;
  font-size: 2rem;
  margin-bottom: 40px;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.video-container {
  max-width: 100%;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 10px 50px rgba(220, 38, 38, 0.3);
  background: rgba(20, 20, 20, 0.8);
  border: 2px solid rgba(220, 38, 38, 0.3);
  padding: 3px;
}

.video-container video {
  width: 100%;
  height: auto;
  display: block;
  border-radius: 16px;
}

@media (max-width: 768px) {
  .video-section {
    margin-top: 60px;
    margin-bottom: 60px;
  }

  .video-section h2 {
    font-size: 1.5rem;
    margin-bottom: 30px;
  }

  .video-container {
    border-radius: 15px;
  }

  .video-container video {
    border-radius: 12px;
  }
}
</style>
